import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Download, Eye, CheckCircle, 
  XCircle, Clock, User, FileText, Shield,
  AlertCircle, ChevronLeft, ChevronRight,
  Globe, Calendar, CreditCard, Mail,
  MoreVertical, RefreshCw, TrendingUp,
  TrendingDown, BarChart3, UserPlus,
  MapPin, IdCard, Image, Home,
  Copy, ExternalLink, ArrowRight,
  Users, FileCheck, ShieldCheck,
  Percent, Clock3, UserCheck
} from 'lucide-react';
import { AdminKYCAPI, AdminAPIUtils } from '../services/api';

function KYC() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    startDate: '',
    endDate: '',
    documentType: 'all'
  });
  const [actionMenu, setActionMenu] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchSubmissions();
  }, [pagination.page, filters]);

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const data = await AdminKYCAPI.getKYCStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch KYC stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (filters.status !== 'all') {
        params.status = filters.status;
      }

      if (filters.startDate) {
        params.start_date = filters.startDate;
      }

      if (filters.endDate) {
        params.end_date = filters.endDate;
      }

      const response = await AdminKYCAPI.getKYCSubmissions(params);
      setSubmissions(response.submissions || []);
      setPagination({
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 20,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.pages || 1
      });
    } catch (err) {
      setError(AdminAPIUtils.handleError(err, false).message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchSubmissions();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      status: 'all',
      startDate: '',
      endDate: '',
      documentType: 'all'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const downloadDocument = async (kycId, documentType) => {
    try {
      await AdminKYCAPI.downloadDocument(kycId, documentType);
    } catch (err) {
      AdminAPIUtils.handleError(err);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      0: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', icon: <Clock3 className="w-3 h-3" /> },
      1: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', icon: <CheckCircle className="w-3 h-3" /> },
      2: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/20', icon: <XCircle className="w-3 h-3" /> }
    };
    
    const labels = {
      0: 'Pending',
      1: 'Verified',
      2: 'Rejected'
    };
    
    const configItem = config[status] || { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', icon: <Clock className="w-3 h-3" /> };
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${configItem.bg} ${configItem.text} border ${configItem.border} flex items-center gap-1.5`}>
        {configItem.icon}
        {labels[status] || 'Unknown'}
      </span>
    );
  };

  const getDocumentTypeIcon = (type) => {
    const icons = {
      'passport': <Globe className="w-4 h-4 text-blue-400" />,
      'id_card': <IdCard className="w-4 h-4 text-purple-400" />,
      'drivers_license': <CreditCard className="w-4 h-4 text-emerald-400" />,
      'residence_permit': <Home className="w-4 h-4 text-amber-400" />
    };
    
    return icons[type] || <FileText className="w-4 h-4 text-gray-400" />;
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      'passport': 'Passport',
      'id_card': 'National ID',
      'drivers_license': 'Driver License',
      'residence_permit': 'Residence Permit'
    };
    
    return labels[type] || type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const statCards = [
    {
      title: 'Total Submissions',
      value: stats?.total_submissions || 0,
      icon: <FileCheck className="w-5 h-5" />,
      color: 'from-blue-500 to-cyan-500',
      change: '24',
      changeText: 'this week',
      trend: 'up'
    },
    {
      title: 'Pending Review',
      value: stats?.pending || 0,
      icon: <Clock className="w-5 h-5" />,
      color: 'from-amber-500 to-yellow-500',
      change: '48h',
      changeText: 'avg response time',
      trend: 'neutral'
    },
    {
      title: 'Verified',
      value: (stats?.by_status || []).find(s => s.status == 1)?.count || 0,
      icon: <ShieldCheck className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-500',
      change: '92%',
      changeText: 'approval rate',
      trend: 'up'
    },
    {
      title: 'Rejected',
      value: (stats?.by_status || []).find(s => s.status == 2)?.count || 0,
      icon: <XCircle className="w-5 h-5" />,
      color: 'from-rose-500 to-red-500',
      change: '8%',
      changeText: 'rejection rate',
      trend: 'neutral'
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
            KYC Management
          </h1>
          <p className="text-charcoal-gray mt-1">Verify user identities and validate document authenticity</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchSubmissions}
            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02]"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div 
            key={index} 
            className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:bg-night-blue/70 transition-all duration-300 hover:scale-[1.02] group"
          >
            <div className="flex items-start justify-between mb-6">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${card.color} shadow-lg`}>
                {card.icon}
              </div>
              {card.trend === 'up' && (
                <div className="flex items-center text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">+{card.change}</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold text-white">{card.value}</p>
              <p className="text-charcoal-gray text-sm uppercase tracking-wider">{card.title}</p>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-800">
              <p className="text-sm text-charcoal-gray">
                <span className={`font-medium ${card.trend === 'up' ? 'text-emerald-400' : card.trend === 'down' ? 'text-rose-400' : 'text-gray-400'}`}>
                  {card.trend === 'up' ? '+' : ''}{card.change}
                </span>{' '}
                {card.changeText}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters Card */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-charcoal-gray w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by username, email, or document number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all duration-300"
                  />
                </div>
              </div>
              <button
                type="submit"
                className="px-5 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02]"
              >
                Search
              </button>
            </form>
          </div>

          {/* Status Filter */}
          <div>
            <div className="flex items-center gap-3">
              <Filter className="text-charcoal-gray w-5 h-5" />
              <select
                value={filters.status}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, status: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
              >
                <option value="all">All Status</option>
                <option value="0">Pending</option>
                <option value="1">Verified</option>
                <option value="2">Rejected</option>
              </select>
            </div>
          </div>

          {/* Document Type Filter */}
          <div>
            <div className="flex items-center gap-3">
              <FileText className="text-charcoal-gray w-5 h-5" />
              <select
                value={filters.documentType}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, documentType: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
              >
                <option value="all">All Document Types</option>
                <option value="passport">Passport</option>
                <option value="id_card">National ID</option>
                <option value="drivers_license">Driver License</option>
                <option value="residence_permit">Residence Permit</option>
              </select>
            </div>
          </div>
        </div>

        {/* Date Range Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div>
            <label className="block text-sm font-medium text-charcoal-gray mb-2">Start Date</label>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-charcoal-gray w-4 h-4" />
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-charcoal-gray mb-2">End Date</label>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-charcoal-gray w-4 h-4" />
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Filter Actions */}
        <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-800">
          <div className="text-sm text-charcoal-gray">
            Showing <span className="text-white font-medium">{submissions.length}</span> of{' '}
            <span className="text-white font-medium">{pagination.total}</span> submissions
          </div>
          <button
            onClick={clearFilters}
            className="text-sm text-charcoal-gray hover:text-white transition-colors hover:bg-gray-800/50 px-3 py-1.5 rounded-lg"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* KYC Submissions Table */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
              <p className="text-charcoal-gray">Loading KYC submissions...</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-rose-400 mx-auto mb-4" />
            <div className="text-rose-300 mb-4 font-medium">Error: {error}</div>
            <button
              onClick={fetchSubmissions}
              className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300"
            >
              Retry
            </button>
          </div>
        ) : submissions.length === 0 ? (
          <div className="p-8 text-center">
            <Shield className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No KYC submissions found</h3>
            <p className="text-charcoal-gray mb-6">Try adjusting your search or filters</p>
            <button
              onClick={clearFilters}
              className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-800">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4" />
                        User
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <IdCard className="w-4 h-4" />
                        Document
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Status
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Submitted
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {submissions.map((submission) => (
                    <tr 
                      key={submission.id} 
                      className="hover:bg-gray-800/30 transition-colors group"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="relative">
                            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                              {submission.username?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-night-blue ${
                              submission.status === 1 ? 'bg-emerald-500' : 
                              submission.status === 2 ? 'bg-rose-500' : 
                              'bg-amber-500'
                            }`} />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white group-hover:text-sky-300 transition-colors">
                              {submission.username}
                            </div>
                            <div className="text-sm text-charcoal-gray truncate max-w-[160px]">{submission.email}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              <div className="flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                {submission.document_country} 
                                <span className="text-gray-600 mx-1">•</span>
                                <span className="truncate max-w-[120px]">{submission.document_number?.substring(0, 12)}...</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-gray-800/50 rounded-lg">
                            {getDocumentTypeIcon(submission.document_type)}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white">
                              {getDocumentTypeLabel(submission.document_type)}
                            </div>
                            <div className="text-xs text-charcoal-gray">
                              Expires: {formatDate(submission.document_expiry_date)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          {getStatusBadge(submission.status)}
                          {submission.verified_at && submission.status !== 0 && (
                            <div className="text-xs text-charcoal-gray">
                              {AdminAPIUtils.timeAgo(submission.verified_at)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="text-sm text-white">{formatDate(submission.submitted_at)}</div>
                          <div className="text-xs text-charcoal-gray">
                            {AdminAPIUtils.timeAgo(submission.submitted_at)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => navigate(`/kyc/${submission.id}`)}
                            className="p-2 bg-gray-800/50 hover:bg-sky-500/20 text-sky-400 hover:text-sky-300 rounded-lg transition-all duration-300 hover:scale-110"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          
                          <div className="relative">
                            <button
                              onClick={() => setActionMenu(actionMenu === submission.id ? null : submission.id)}
                              className="p-2 bg-gray-800/50 hover:bg-gray-700 text-gray-400 hover:text-white rounded-lg transition-all duration-300 hover:scale-110"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                            {actionMenu === submission.id && (
                              <div className="absolute right-0 mt-2 w-56 bg-night-blue border border-gray-800 rounded-xl shadow-2xl py-2 z-50">
                                <div className="px-4 py-2 border-b border-gray-800">
                                  <p className="text-sm font-medium text-white">Document Actions</p>
                                </div>
                                <button
                                  onClick={() => {
                                    downloadDocument(submission.id, 'document_front');
                                    setActionMenu(null);
                                  }}
                                  className="w-full px-4 py-2.5 text-sm text-blue-400 hover:bg-blue-500/10 flex items-center gap-2 transition-colors"
                                >
                                  <Download className="w-4 h-4" />
                                  Download Front
                                </button>
                                <button
                                  onClick={() => {
                                    downloadDocument(submission.id, 'document_back');
                                    setActionMenu(null);
                                  }}
                                  className="w-full px-4 py-2.5 text-sm text-blue-400 hover:bg-blue-500/10 flex items-center gap-2 transition-colors"
                                >
                                  <Download className="w-4 h-4" />
                                  Download Back
                                </button>
                                <button
                                  onClick={() => {
                                    downloadDocument(submission.id, 'selfie_with_document');
                                    setActionMenu(null);
                                  }}
                                  className="w-full px-4 py-2.5 text-sm text-purple-400 hover:bg-purple-500/10 flex items-center gap-2 transition-colors"
                                >
                                  <Image className="w-4 h-4" />
                                  Download Selfie
                                </button>
                                <button
                                  onClick={() => {
                                    navigate(`/users/${submission.user_id}`);
                                    setActionMenu(null);
                                  }}
                                  className="w-full px-4 py-2.5 text-sm text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-2 transition-colors"
                                >
                                  <User className="w-4 h-4" />
                                  View User Profile
                                </button>
                                <div className="px-4 py-2 border-t border-gray-800">
                                  <p className="text-xs text-charcoal-gray">KYC ID: {submission.id}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-charcoal-gray">
                    Page <span className="font-medium text-white">{pagination.page}</span> of{' '}
                    <span className="font-medium text-white">{pagination.totalPages}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page === 1}
                      className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                        pagination.page === 1
                          ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <div className="flex items-center space-x-1">
                      {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                            className={`px-4 py-2.5 min-w-[40px] rounded-xl transition-all duration-300 ${
                              pagination.page === pageNum
                                ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg'
                                : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
                        <>
                          <span className="px-2 text-charcoal-gray">...</span>
                          <button
                            onClick={() => setPagination(prev => ({ ...prev, page: pagination.totalPages }))}
                            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl hover:scale-105 transition-all duration-300"
                          >
                            {pagination.totalPages}
                          </button>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }))}
                      disabled={pagination.page === pagination.totalPages}
                      className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                        pagination.page === pagination.totalPages
                          ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                      }`}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default KYC;